/**
 * The one piece of plumbing every post pass shares: a clip-space triangle, a
 * `RawShaderMaterial`, and a draw call.
 *
 * Deliberately not `EffectComposer`. That class round-trips through a pair of
 * matched LDR-by-default targets and owns the swap order, which is wrong for a
 * chain that mixes resolutions (full, half, eighth), formats and blend modes
 * the way a dual-filter bloom does. Owning the targets outright is less code
 * here and far less fighting later.
 */

import * as THREE from 'three';
import { FULLSCREEN_VERT } from '../shaders/common';

export type UniformMap = Record<string, THREE.IUniform>;

// One triangle, one scene, one camera for the whole process. Passes swap the
// material on the shared mesh, so no pass allocates scene-graph state.
let geometry: THREE.BufferGeometry | null = null;
let quad: THREE.Mesh | null = null;
let quadScene: THREE.Scene | null = null;
let quadCamera: THREE.Camera | null = null;
let placeholder: THREE.MeshBasicMaterial | null = null;
let refCount = 0;

function acquire(): void {
  refCount++;
  if (geometry !== null) return;

  geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3),
  );

  quadCamera = new THREE.Camera();
  quadScene = new THREE.Scene();
  quadScene.matrixWorldAutoUpdate = false;

  placeholder = new THREE.MeshBasicMaterial();
  quad = new THREE.Mesh(geometry, placeholder);
  quad.frustumCulled = false;
  quad.matrixAutoUpdate = false;
  quadScene.add(quad);
}

function release(): void {
  refCount--;
  if (refCount > 0 || geometry === null) return;
  geometry.dispose();
  placeholder?.dispose();
  geometry = null;
  placeholder = null;
  quad = null;
  quadScene = null;
  quadCamera = null;
}

export type PassBlending = 'replace' | 'add';

/** A single fullscreen draw with its own program and uniform set. */
export class FullscreenPass {
  readonly material: THREE.RawShaderMaterial;
  readonly uniforms: UniformMap;

  constructor(fragmentShader: string, uniforms: UniformMap, blending: PassBlending = 'replace') {
    acquire();
    this.uniforms = uniforms;
    this.material = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: FULLSCREEN_VERT,
      fragmentShader,
      uniforms,
      depthTest: false,
      depthWrite: false,
      transparent: blending === 'add',
    });

    if (blending === 'add') {
      // Explicit factors rather than AdditiveBlending so alpha accumulates the
      // same way colour does instead of following the premultiplied default.
      this.material.blending = THREE.CustomBlending;
      this.material.blendEquation = THREE.AddEquation;
      this.material.blendSrc = THREE.OneFactor;
      this.material.blendDst = THREE.OneFactor;
      this.material.blendEquationAlpha = THREE.AddEquation;
      this.material.blendSrcAlpha = THREE.OneFactor;
      this.material.blendDstAlpha = THREE.OneFactor;
    } else {
      this.material.blending = THREE.NoBlending;
    }
  }

  /** Draws into `target`, or the default framebuffer when `target` is null. */
  render(
    renderer: THREE.WebGLRenderer,
    target: THREE.WebGLRenderTarget | null,
    clear = false,
  ): void {
    if (quad === null || quadScene === null || quadCamera === null) return;
    quad.material = this.material;
    renderer.setRenderTarget(target);
    if (clear) renderer.clear(true, false, false);
    renderer.render(quadScene, quadCamera);
  }

  dispose(): void {
    this.material.dispose();
    release();
  }
}

export interface TargetOptions {
  type: THREE.TextureDataType;
  /** Nearest keeps the pixel grid intact; linear is for anything already blurred. */
  filter?: THREE.MagnificationTextureFilter;
  depthBuffer?: boolean;
}

/**
 * An HDR colour target with the settings this project needs everywhere: no
 * mipmaps, clamped edges, linear working space, RGBA so half-float is
 * renderable on every driver that reports the extension.
 */
export function createTarget(
  width: number,
  height: number,
  options: TargetOptions,
): THREE.WebGLRenderTarget {
  const filter = options.filter ?? THREE.NearestFilter;
  const target = new THREE.WebGLRenderTarget(Math.max(1, width), Math.max(1, height), {
    format: THREE.RGBAFormat,
    type: options.type,
    minFilter: filter,
    magFilter: filter,
    wrapS: THREE.ClampToEdgeWrapping,
    wrapT: THREE.ClampToEdgeWrapping,
    generateMipmaps: false,
    depthBuffer: options.depthBuffer ?? false,
    stencilBuffer: false,
    colorSpace: THREE.LinearSRGBColorSpace,
  });
  target.texture.name = `target-${width}x${height}`;
  return target;
}

/** Clears a target to opaque black. Used to prime feedback buffers. */
export function clearTarget(renderer: THREE.WebGLRenderer, target: THREE.WebGLRenderTarget): void {
  const prev = renderer.getRenderTarget();
  renderer.setRenderTarget(target);
  renderer.clear(true, true, false);
  renderer.setRenderTarget(prev);
}
